import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import authAPI from "../api/auth";
import passkeysAPI from "../api/passkeys";
import accountsAPI from "../api/accounts";
import axios from "axios";
import logger from "utils/logger";
import storage from "utils/storage";
import { Dispatch } from "redux";
import { navigate } from "../config/navigate";
import { clearDeepLinkAccountParam, getDeepLinkAccountId, pickAccount } from "../utils/deepLinkAccount";
import pendingInvitation from "utils/pendingInvitation";
import { startAuthentication } from "@simplewebauthn/browser";
import { forgetPasskeyOnDevice, isPasskeyCancel, rememberPasskeyBrowser } from "utils/passkeys";

export interface AuthState {
  isSignedIn: boolean;
  isLoading?: boolean;
  currentUser: any;
  currentAccount: any;
  accounts: any[];
  adminView: boolean;
  errors?: string[];
  // Set while sign-in waits for the code owl emailed (RM-3849).
  loginCode?: LoginCodeState | null;
}

export interface LoginCodeState {
  challengeToken: string;
  emailHint: string;
}

const initialState: AuthState = {
  isSignedIn: false,
  currentUser: null,
  currentAccount: null,
  accounts: [],
  adminView: false,
};

interface SignInSuccessPayload {
  user: any;
  accounts: any[];
  currentAccount: number | null;
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    signInStart(state) {
      state.isSignedIn = false;
      state.isLoading = true;
    },
    signInSuccess(state, action: PayloadAction<SignInSuccessPayload>) {
      state.isSignedIn = true;
      state.isLoading = false;
      state.errors = [];
      state.currentUser = action.payload.user;
      state.accounts = action.payload.accounts;
      state.currentAccount = action.payload.currentAccount;
      state.adminView = false;
    },
    signInFailure(state, action: PayloadAction<string[]>) {
      state.isLoading = false;
      state.errors = action.payload;
    },
    loginCodeRequired(state, action: PayloadAction<LoginCodeState>) {
      state.isLoading = false;
      state.errors = [];
      state.loginCode = action.payload;
    },
    loginCodeCleared(state) {
      state.isLoading = false;
      state.loginCode = null;
    },
    signOut(state) {
      state.isSignedIn = false;
      state.isLoading = false;
      state.currentUser = undefined as any;
      state.currentAccount = undefined as any;
    },
    setCurrentAccount(state, action: PayloadAction<number>) {
      state.currentAccount = action.payload;
    },
    switchToAdminView(state, action: PayloadAction<boolean>) {
      state.adminView = action.payload;
    },
    updateCurrentUser(state, action: PayloadAction<Record<string, any>>) {
      if (state.currentUser) {
        state.currentUser = {
          ...state.currentUser,
          data: {
            ...state.currentUser?.data,
            ...action.payload,
          },
        };
      }
    },
  },
});

export const {
  signInStart,
  signInSuccess,
  signInFailure,
  loginCodeRequired,
  loginCodeCleared,
  signOut,
  setCurrentAccount,
  switchToAdminView,
  updateCurrentUser,
} = authSlice.actions;

const saveSelectedAccount = (account: string | number) => {
  storage.set("currentAccount", String(account));
  axios.defaults.headers.common["account"] = String(account);
};

export const setAccount = (index: number) => {
  return (dispatch: Dispatch, getState: () => any) => {
    const { accounts } = getState().auth;
    const currentAccount = accounts[index];

    saveSelectedAccount(currentAccount.id);
    dispatch(setCurrentAccount(currentAccount.id));
  };
};

// After credentials are accepted (sign-in, invitation registration or
// acceptance): load the accounts and select `preferredAccountId` when present,
// else a `?account=` deep link, else the first. Then land on the pending
// invitation if the user signed in to accept one, stay on the deep-linked page,
// or go home.
export const completeSignIn = (user: any, preferredAccountId?: number) => {
  return (dispatch: Dispatch) =>
    accountsAPI
      .getAccounts()
      .then((accounts) => {
        const invitationKey = pendingInvitation.take();
        if (accounts.length === 0) {
          dispatch(signInSuccess({ user, accounts, currentAccount: null }));
          navigate(invitationKey ? `/invitation/${invitationKey}` : "/platform");
        } else {
          const deepLinkId = getDeepLinkAccountId();
          const currentAccount =
            accounts.find((a: any) => a.id === preferredAccountId) || pickAccount(accounts, deepLinkId, null);
          saveSelectedAccount(currentAccount.id);
          dispatch(signInSuccess({ user, accounts, currentAccount: currentAccount.id }));
          if (invitationKey) {
            navigate(`/invitation/${invitationKey}`);
          } else if (deepLinkId) {
            // Keep the user on the deep-linked page they signed in from.
            clearDeepLinkAccountParam();
          } else {
            navigate("/");
          }
        }
      })
      .catch((err) => {
        if (err.response) {
          dispatch(signInFailure(err.response.data.errors));
        } else {
          logger.error(err);
        }
      });
};

// owl answers bad credentials with `errors` (a list) but a rate limit (429)
// with a single `error` string, so accept either.
const responseErrors = (data: any): string[] =>
  data?.errors ?? [data?.error ?? "Something went wrong. Please try again."];

export const signInUser = ({ email, password }: { email: string; password: string }) => {
  return (dispatch: Dispatch) => {
    dispatch(signInStart());
    return authAPI
      .signInUser({ email, password })
      .then((res) => {
        if (res.errors) {
          dispatch(signInFailure(res.errors));
          return false;
        }
        if (res.data?.otp_required) {
          dispatch(loginCodeRequired({ challengeToken: res.data.challenge_token, emailHint: res.data.email_hint }));
          return false;
        }
        completeSignIn(res.data)(dispatch);
      })
      .catch((err) => {
        if (err.response) {
          dispatch(signInFailure(responseErrors(err.response.data)));
        } else {
          logger.error(err);
        }
      });
  };
};

// Second sign-in step: the emailed code. On success the session headers are
// stored by the response interceptor, like a normal sign-in. When owl says the
// sign-in itself expired (`restart`), go back to the password form.
export const verifyLoginCode = ({ code, rememberDevice }: { code: string; rememberDevice: boolean }) => {
  return (dispatch: Dispatch, getState: () => any) => {
    const { loginCode } = getState().auth;
    if (!loginCode) return Promise.resolve();
    dispatch(signInStart());
    return authAPI
      .verifyLoginCode({ challengeToken: loginCode.challengeToken, code, rememberDevice })
      .then((res) => {
        if (res.data?.trusted_device_token) {
          storage.set("trusted-device-token", res.data.trusted_device_token);
        }
        // Keep the code step up until the accounts load and we navigate away;
        // clearing it first flashed the password form for a second.
        return completeSignIn({ data: res.data.data })(dispatch).then(() => {
          dispatch(loginCodeCleared());
        });
      })
      .catch((err) => {
        if (!err.response) {
          logger.error(err);
          return;
        }
        if (err.response.data?.restart) dispatch(loginCodeCleared());
        dispatch(signInFailure(responseErrors(err.response.data)));
      });
  };
};

// Passkey sign-in (RM-3845): no email, password or emailed code. `autofill`
// starts the request quietly so the browser can offer passkeys in the email
// field's autofill menu; it stays pending until one is picked, and errors are
// not shown because the user never asked for anything.
export const signInWithPasskey = ({ autofill = false }: { autofill?: boolean } = {}) => {
  return async (dispatch: Dispatch) => {
    let ceremony;
    try {
      ceremony = await passkeysAPI.signInOptions();
    } catch (err: any) {
      if (!autofill) dispatch(signInFailure(responseErrors(err?.response?.data)));
      return;
    }

    let credential;
    try {
      credential = await startAuthentication({ optionsJSON: ceremony.options, useBrowserAutofill: autofill });
    } catch (err) {
      if (!autofill && !isPasskeyCancel(err)) {
        logger.error(err);
        dispatch(signInFailure(["Your device couldn't use a passkey. Sign in with your password instead."]));
      }
      return;
    }

    dispatch(signInStart());
    try {
      const res = await passkeysAPI.verifySignIn(ceremony.challenge_id, credential);
      rememberPasskeyBrowser();
      await completeSignIn(res.data)(dispatch);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.unknown_credential) await forgetPasskeyOnDevice(data.rp_id, data.credential_id);
      if (err?.response) {
        dispatch(signInFailure(responseErrors(data)));
      } else {
        logger.error(err);
        dispatch(signInFailure(responseErrors(undefined)));
      }
    }
  };
};

// Resolves to an error message to show, or null when a new code was sent.
export const resendLoginCode = () => {
  return (dispatch: Dispatch, getState: () => any): Promise<string | null> => {
    const { loginCode } = getState().auth;
    if (!loginCode) return Promise.resolve(null);
    return authAPI
      .resendLoginCode(loginCode.challengeToken)
      .then(() => null)
      .catch((err) => {
        if (err.response?.data?.restart) {
          dispatch(loginCodeCleared());
          dispatch(signInFailure(responseErrors(err.response.data)));
          return null;
        }
        return responseErrors(err.response?.data)[0];
      });
  };
};

// Back from the code step to the email and password form.
export const cancelLoginCode = () => (dispatch: Dispatch) => {
  dispatch(loginCodeCleared());
  dispatch(signInFailure([]));
};

export const validateUser = () => {
  return (dispatch: Dispatch) => {
    authAPI
      .validateUser()
      .then((res) => {
        accountsAPI
          .getAccounts()
          .then((accounts) => {
            if (accounts.length === 0) {
              dispatch(signInSuccess({ user: res.data, accounts, currentAccount: null }));
              navigate("/platform");
            } else {
              const storedId = +storage.get("currentAccount")!;
              const account = pickAccount(accounts, getDeepLinkAccountId(), storedId);
              saveSelectedAccount(account.id);
              dispatch(signInSuccess({ user: res.data, accounts, currentAccount: account.id }));
              clearDeepLinkAccountParam();
            }
          })
          .catch((err) => {
            if (err.response) {
              dispatch(signInFailure(err.response.data.errors));
            } else {
              logger.error(err);
            }
          });
      })
      .catch((err) => {
        if (err.response) {
          (dispatch as any)(signOutUser());
        } else {
          logger.error(err);
        }
      });
  };
};

export const signOutUser = () => {
  return (dispatch: Dispatch) => {
    storage.remove("access-token");
    dispatch(signOut());
    navigate("/signin");
  };
};

export default authSlice.reducer;
